/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect} from 'react'
import {
    isHydrated,
    getEnvStaleTime,
    getEnvRefetchOnMount,
    getPageDesignerRegion
} from '../../../utils/utils'
import {usePage} from '@salesforce/commerce-sdk-react'
import {PageContext, Component} from '@salesforce/commerce-sdk-react/components'
import {PDCHeaderTopBanner} from '../../../page-designer/assets'
import {HEADER_PDC_PAGE_ID} from '../../header/constants'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_assets.pdcHeaderTopBanner': PDCHeaderTopBanner
}

/**
 * AboveHeader component to render the above header banner
 * @returns {React.ReactElement}
 */
const AboveHeader = () => {
    const staleTime = getEnvStaleTime()
    const refetchOnMount = getEnvRefetchOnMount()
    const {data: headerPage, isFetchedAfterMount} = usePage(
        {parameters: {pageId: HEADER_PDC_PAGE_ID}},
        {staleTime, refetchOnMount}
    )
    const headerTopBannerComponent = getPageDesignerRegion(headerPage, 'main')?.components?.find(
        (comp) => comp?.typeId === 'commerce_assets.pdcHeaderTopBanner'
    )
    const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})
    const [showBanner, setShowBanner] = useState(headerTopBannerComponent !== undefined)

    useEffect(() => {
        if (!isHydrated()) return

        const hideBannerOnScroll = () => {
            setShowBanner(window.scrollY === 0)
        }

        window.addEventListener('scroll', hideBannerOnScroll)

        return () => {
            window.removeEventListener('scroll', hideBannerOnScroll)
        }
    }, [isHydrated()])

    /**
     * Stale data fix.
     * This component's content is rendered on the server with stale data.
     * To avoid flashes of incorrect data, we show the content after fresh data is fetched on the client: isFetchedAfterMount.
     * If refetchOnMount is not 'always', then use the default behavior.
     */
    const staleDataFix =
        (refetchOnMount === 'always' && isFetchedAfterMount) || refetchOnMount !== 'always'

    return (
        staleDataFix &&
        showBanner &&
        headerTopBannerComponent && (
            <PageContext.Provider value={contextValue}>
                <Component component={headerTopBannerComponent} />
            </PageContext.Provider>
        )
    )
}

export default AboveHeader
